/**
 * useProfile — RC 1.20A
 *
 * Lightweight hook that fetches (and caches for the session) the
 * current user's profile row. Exposes a `greetingName` that always
 * prefers nickname → first_name. **Never** falls back to the email
 * prefix.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export function greetingNameFrom(profile) {
  if (!profile) return null;
  const nick = (profile.nickname || '').trim();
  if (nick) return nick;
  const first = (profile.first_name || '').trim();
  if (first) return first;
  // Last-resort: use the first word of legacy full_name.
  const full = (profile.full_name || '').trim();
  if (full) return full.split(/\s+/)[0];
  return null;
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) { setProfile(null); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, first_name, last_name, nickname, is_admin')
          .eq('id', user.id)
          .maybeSingle();
        if (!alive) return;
        if (error) { console.warn('[useProfile]', error.message); setProfile(null); }
        else setProfile(data || null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  return { profile, loading, greetingName: greetingNameFrom(profile) };
}
